import { useState, useCallback, useMemo } from 'react';

import {
  useGetProfitAndLoss,
  ProfitAndLossFilter,
  ProfitAndLossApiResponse,
} from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';

interface UseProfitAndLossOptions {
  recordId?: number;
}

// Helper to get default date range (1 year back to today)
const getDefaultDateRange = () => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `${formatDate(oneYearAgo)} 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

// Types for mapped profit and loss data
export interface ProfitAndLossItem {
  name: string;
  amount: number;
}

export interface MappedProfitAndLossData {
  debitSide: {
    items: ProfitAndLossItem[];
    total: number;
  };
  creditSide: {
    items: ProfitAndLossItem[];
    total: number;
  };
}

const useProfitAndLoss = (options: UseProfitAndLossOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  // Use provided recordId or fall back to selected record
  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  // Get default date range
  const defaultDates = getDefaultDateRange();

  // Filter state - include default dates as API requires them
  const [filters, setFilters] = useState<Partial<ProfitAndLossFilter>>({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
  });

  // API hook - only fetch when we have required params
  const hasRequiredParams = !!recordId && !!userId && !!filters.start_date && !!filters.end_date;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetProfitAndLoss({
    filters: {
      ...filters,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  // Raw data
  const rawData = useMemo(() => data?.data, [data?.data]);

  // Helper to parse amount (API can return number or string)
  const parseAmount = (value: number | string): number => {
    if (typeof value === 'string') {
      return parseFloat(value) || 0;
    }
    return value;
  };

  // Map API response to display format
  const mappedData = useMemo((): MappedProfitAndLossData | null => {
    if (!rawData) return null;

    const response = rawData as ProfitAndLossApiResponse;

    // Process Debit Side
    const debitItems: ProfitAndLossItem[] = [];
    if (response.debit) {
      Object.entries(response.debit).forEach(([name, value]) => {
        debitItems.push({ name, amount: parseAmount(value) });
      });
    }

    // Process Credit Side
    const creditItems: ProfitAndLossItem[] = [];
    if (response.credit) {
      Object.entries(response.credit).forEach(([name, value]) => {
        creditItems.push({ name, amount: parseAmount(value) });
      });
    }

    return {
      debitSide: {
        items: debitItems,
        total: response.total_debit ?? 0,
      },
      creditSide: {
        items: creditItems,
        total: response.total_credit ?? 0,
      },
    };
  }, [rawData]);

  // Filter handlers
  const updateFilter = useCallback((updates: Partial<ProfitAndLossFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    const defaults = getDefaultDateRange();
    updateFilter({
      start_date: startDate ? `${startDate} 00:00:00` : defaults.start_date,
      end_date: endDate ? `${endDate} 23:59:59` : defaults.end_date,
    });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    const defaultDates = getDefaultDateRange();
    setFilters({
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    });
  }, []);

  return {
    // Data
    rawData,
    mappedData,

    // Loading states
    isLoading,
    hasRequiredParams,

    // Error state
    error,

    // Filter state and handlers
    filters,
    handleDateFilter,
    resetFilters,
    updateFilter,

    // Refetch
    refetch,
  };
};

export default useProfitAndLoss;
